/**
 * TransferFundsModal - Modal for transferring wallet funds to teammates
 * Allows users to send funds from their wallet to other locations they've added as team members
 * Uses atomic RPC for secure transfers
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ArrowRightLeft, Loader2, AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { AddBalanceModal } from './AddBalanceModal';
import { TeammateSelector, type Teammate } from './TeammateSelector';
import { TeamManagementPanel } from './TeamManagementPanel';

interface TransferFundsModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  currentBalance: number;
  autoRechargeThreshold: number;
  onTransferComplete: (newBalance: number) => void;
}

const MIN_AMOUNT = 0.01;
const MAX_AMOUNT = 10000;

export function TransferFundsModal({
  isOpen,
  onClose,
  locationId,
  currentBalance,
  autoRechargeThreshold,
  onTransferComplete,
}: TransferFundsModalProps) {
  // Amount state
  const [amount, setAmount] = useState('');

  // Teammate state
  const [teammates, setTeammates] = useState<Teammate[]>([]);
  const [selectedTeammate, setSelectedTeammate] = useState<Teammate | null>(null);
  const [loadingTeammates, setLoadingTeammates] = useState(false);

  // Team member management state
  const [showTeamManagement, setShowTeamManagement] = useState(false);

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  // Stacked Add Balance modal state
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);

  // Live balance (updated when Add Balance completes)
  const [liveBalance, setLiveBalance] = useState(currentBalance);

  // Update live balance when prop changes
  useEffect(() => {
    setLiveBalance(currentBalance);
  }, [currentBalance]);

  const formatCurrency = (amt: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amt);
  };

  // Calculate transfer values
  const effectiveAmount = parseFloat(amount) || 0;
  const remainingBalance = liveBalance - effectiveAmount;
  const isValidAmount = effectiveAmount >= MIN_AMOUNT && effectiveAmount <= MAX_AMOUNT && effectiveAmount <= liveBalance;
  const isBelowThreshold = remainingBalance < autoRechargeThreshold;
  const shortfall = isBelowThreshold ? autoRechargeThreshold - remainingBalance : 0;

  // Can transfer only if: valid amount, teammate selected, remaining >= threshold
  const canTransfer = isValidAmount &&
    !!selectedTeammate &&
    !isBelowThreshold &&
    effectiveAmount > 0;

  // Fetch teammates
  const fetchTeammates = useCallback(async (autoSelectFirst = false) => {
    setLoadingTeammates(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/team-members`,
        { credentials: 'include' }
      );
      const data = await response.json() as {
        success: boolean;
        team_members?: Teammate[];
        error?: string;
      };

      if (data.success && data.team_members) {
        setTeammates(data.team_members);
        // Auto-select first teammate only on initial load
        if (autoSelectFirst && data.team_members.length > 0) {
          setSelectedTeammate(data.team_members[0]);
        }
      }
    } catch (err) {
      console.error('[TransferFundsModal] Error fetching teammates:', err);
    } finally {
      setLoadingTeammates(false);
    }
  }, [locationId]);

  // Reset state and fetch teammates when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset state on open
      setAmount('');
      setSelectedTeammate(null);
      setShowTeamManagement(false);
      setError(null);
      setLiveBalance(currentBalance);
      // Fetch teammates with auto-select on initial open
      fetchTeammates(true);
    }
  }, [isOpen, currentBalance, fetchTeammates]);

  // Add: append teammate, auto-select it, collapse the management panel.
  const handleTeammateAdded = (teammate: Teammate) => {
    setTeammates(prev => [...prev, teammate]);
    setSelectedTeammate(teammate);
    setShowTeamManagement(false);
  };

  // Remove: drop from list, deselect if it was the active selection.
  const handleTeammateRemoved = (teammate: Teammate) => {
    setTeammates(prev => prev.filter(t => t.id !== teammate.id));
    if (selectedTeammate?.location_id === teammate.location_id) {
      const remaining = teammates.filter(t => t.id !== teammate.id);
      setSelectedTeammate(remaining.length > 0 ? remaining[0] : null);
    }
  };

  // Handle balance added from stacked modal
  const handleBalanceAdded = (newBalance: number) => {
    setLiveBalance(newBalance);
    setShowAddBalanceModal(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isSubmittingRef.current || !canTransfer) return;
    isSubmittingRef.current = true;

    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/transfer`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            to_location_id: selectedTeammate!.location_id,
            amount: effectiveAmount,
          }),
        }
      );

      const data = await response.json() as {
        success: boolean;
        new_balance?: number;
        error?: string;
      };

      if (data.success && typeof data.new_balance === 'number') {
        onTransferComplete(data.new_balance);
        onClose();
      } else {
        setError(data.error || 'Transfer failed. Please try again.');
      }
    } catch (err) {
      console.error('[TransferFundsModal] Transfer error:', err);
      setError('An error occurred while processing the transfer. Please try again.');
    } finally {
      setProcessing(false);
      isSubmittingRef.current = false;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${showAddBalanceModal ? 'pointer-events-none' : ''}`}>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black/50 transition-opacity ${showAddBalanceModal ? 'bg-black/30' : ''}`}
          onClick={showAddBalanceModal ? undefined : onClose}
        />

        {/* Modal */}
        <div className={`relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col ${showAddBalanceModal ? 'opacity-50' : ''}`}>
          {/* Header - stays fixed at top */}
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Transfer Funds to Teammate</h2>
            </div>
            <button
              onClick={onClose}
              disabled={processing || showAddBalanceModal}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {/* Balance Preview */}
            <div className={`border rounded-lg p-4 ${isBelowThreshold && effectiveAmount > 0 ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-700">Current Balance</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(liveBalance)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-700">Amount to Transfer</span>
                <span className="text-sm font-medium text-red-600">
                  - {formatCurrency(effectiveAmount)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Remaining Funds</span>
                  <span className={`text-lg font-bold ${remainingBalance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatCurrency(remainingBalance)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>Auto-Recharge Threshold</span>
                  <span>{formatCurrency(autoRechargeThreshold)}</span>
                </div>
              </div>

              {/* Status indicator */}
              {effectiveAmount > 0 && (
                <div className="mt-3">
                  {isBelowThreshold ? (
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-orange-700">
                          Add {formatCurrency(shortfall)} to your balance before transferring
                        </p>
                        <button
                          onClick={() => setShowAddBalanceModal(true)}
                          className="mt-2 px-3 py-1.5 bg-orange-600 text-white text-sm font-medium rounded hover:bg-orange-700 transition-colors"
                        >
                          Add Balance Now
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Balance is sufficient</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount to Transfer
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min={MIN_AMOUNT}
                  max={MAX_AMOUNT}
                  step="0.01"
                  disabled={processing || showAddBalanceModal}
                  className="block w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {effectiveAmount > liveBalance && (
                <p className="mt-1 text-xs text-red-600">
                  Amount exceeds your current balance
                </p>
              )}
            </div>

            {/* Teammate Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transfer To
              </label>
              {loadingTeammates ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <TeammateSelector
                  value={selectedTeammate}
                  onChange={setSelectedTeammate}
                  teammates={teammates}
                  onAdjust={() => setShowTeamManagement(true)}
                  disabled={processing || showAddBalanceModal}
                />
              )}
            </div>

            {/* Team Member Management Section */}
            {showTeamManagement && (
              <TeamManagementPanel
                locationId={locationId}
                teammates={teammates}
                onTeammateAdded={handleTeammateAdded}
                onTeammateRemoved={handleTeammateRemoved}
                disabled={processing || showAddBalanceModal}
              />
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Footer - stays fixed at bottom */}
          <div className="flex-shrink-0 flex gap-3 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={processing || showAddBalanceModal}
              className="flex-1 py-2.5 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canTransfer || processing || showAddBalanceModal}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center ${
                canTransfer
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                `Send ${formatCurrency(effectiveAmount)}`
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stacked Add Balance Modal */}
      <AddBalanceModal
        isOpen={showAddBalanceModal}
        onClose={() => setShowAddBalanceModal(false)}
        locationId={locationId}
        currentBalance={liveBalance}
        onBalanceAdded={handleBalanceAdded}
      />
    </>
  );
}
